import { useLocale } from "lib/hooks/useLocale";
import { insertBeforeLastOccurrence, truncateString } from "lib/utils/helpers";
import React from "react";
import blockImage from "assests/images/block-image.png";
import { Minus, Plus } from "@phosphor-icons/react";
import Price from "components/common/Price";
import {
  DiscountedProductSelectorDataType,
  DiscountedProductSelectorItemType,
  DiscountedProductSelectorProductType,
  DiscountedProductSelectorProductVariantType,
} from "lib/types/checkout";
import DiscountedVariantCard from "./DiscountedVariantCard";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { ListCarousel } from "@shopflo/ui/list-carousel";

interface DiscountedProductCardProps {
  key: string;
  selectedProduct: DiscountedProductSelectorProductType;
  groupIndex: number;
  productIndex: number;
}

const DiscountedProductCard: React.FC<DiscountedProductCardProps> = ({
  key,
  selectedProduct,
  groupIndex,
  productIndex,
}) => {
  const { t } = useLocale();
  const {
    state: { discountedProductSelectorData },
    actions: { setDiscountedProductSelectorData },
  } = useCheckoutContext();
  const hasVariants = selectedProduct?.variants?.length > 1;
  const isAddUnavailable =
    discountedProductSelectorData?.items[groupIndex].selectedQuantity ===
      discountedProductSelectorData?.items[groupIndex].quantity ||
    (selectedProduct?.variants?.[0]?.inventoryPolicy === "DENY" &&
      selectedProduct?.variants?.[0]?.inventoryQuantity <= selectedProduct?.variants?.[0]?.selectedQuantity);

  const handleEditProduct = (variantIndex: number, quantity: number) => {
    const selectedGroup = discountedProductSelectorData.items?.[groupIndex];
    const selectedProduct = selectedGroup?.products?.[productIndex];
    const selectedVariant = selectedProduct?.variants?.[variantIndex];

    const modifiedVariants = [...selectedProduct?.variants];
    modifiedVariants[variantIndex] = {
      ...selectedVariant,
      selectedQuantity: selectedVariant?.selectedQuantity + quantity,
    };

    const modifiedProducts = [...selectedGroup?.products];
    modifiedProducts[productIndex] = {
      ...selectedProduct,
      variants: modifiedVariants,
    };

    const modifiedGroups = [...discountedProductSelectorData.items];
    modifiedGroups[groupIndex] = {
      ...selectedGroup,
      products: modifiedProducts,
      selectedQuantity: selectedGroup?.selectedQuantity + quantity,
    };

    const finalData: DiscountedProductSelectorDataType = {
      ...discountedProductSelectorData,
      totalSelectedItems: discountedProductSelectorData.totalSelectedItems + quantity,
      items: modifiedGroups,
    };

    setDiscountedProductSelectorData(finalData);
  };

  const imageUrlForSingleVariant = Boolean(selectedProduct?.variants?.[0]?.imageUrl)
    ? selectedProduct?.variants?.[0]?.imageUrl
    : Boolean(selectedProduct?.primaryImageUrl)
    ? selectedProduct.primaryImageUrl
    : blockImage;

  return (
    <li
      key={key}
      className="relative flex max-h-[9.75rem] w-full flex-col gap-3 overflow-hidden bg-gray-lightest rounded-xl border border-carbon-lighter p-2 select-none">
      {!hasVariants && (
        <div className="flex flex-row gap-2">
          <img
            src={insertBeforeLastOccurrence(imageUrlForSingleVariant, ".jpg", "_small")}
            alt="Cart Item"
            className={`h-[4.875rem] w-[4.875rem] min-w-[4.875rem] rounded-lg border border-gray-light object-cover`}
            draggable={false}
          />
          <div className="flex flex-col w-full justify-between">
            <div className="flex flex-col w-full space-y-2">
              <p className="mt-1 text-xs font-medium text-coal-dark line-clamp-2">{selectedProduct.name}</p>
              {selectedProduct?.variants[0]?.name && (
                <span className="text-sm font-medium text-coal-dark">
                  {selectedProduct?.variants[0]?.name}
                </span>
              )}
            </div>
            <div className="flex w-full items-end justify-between">
              <div className="pr-6">
                <Price
                  total={selectedProduct?.variants[0]?.discountedPrice ?? 0.0}
                  compareAt={selectedProduct?.variants[0]?.currentPrice}
                  orientation="vertical"
                  customCSS="!flex-row !items-center !space-x-1 !text-sm"
                />
              </div>
              {Boolean(selectedProduct?.variants?.[0]?.selectedQuantity) ? (
                <div className="flex flex-row items-center justify-between space-x-1.5 rounded-md border-2 border-primary-dark px-2.5 py-1.5">
                  <button onClick={() => handleEditProduct(0, -1)}>
                    <Minus className="h-4 w-4" />
                  </button>
                  <label className="min-w-[1rem] text-center text-sm">
                    {selectedProduct?.variants?.[0]?.selectedQuantity}
                  </label>
                  <button
                    disabled={isAddUnavailable}
                    onClick={() => handleEditProduct(0, 1)}
                    className={`${isAddUnavailable ? "text-coal-light" : "text-coal-dark"}`}>
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <button
                  disabled={isAddUnavailable}
                  className="flex items-center justify-center space-x-1 rounded-lg border-2 border-primary-dark py-1.5 px-5 text-sm font-medium text-primary-dark disabled:text-opacity-50 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => handleEditProduct(0, 1)}>
                  <Plus size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {hasVariants && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-row gap-2 items-center">
            <img
              src={insertBeforeLastOccurrence(
                Boolean(selectedProduct?.primaryImageUrl) ? selectedProduct.primaryImageUrl : blockImage,
                ".jpg",
                "_small",
              )}
              alt="Cart Item"
              className={`h-[2rem] w-[2rem] min-w-[2rem] rounded-lg border border-gray-light object-cover`}
              draggable={false}
            />
            <span className="text-sm font-medium text-coal-dark">
              {truncateString(selectedProduct.name, 32)}
            </span>
          </div>
          <div className="flex w-full flex-col items-start justify-start space-y-2">
            <ListCarousel
              count={selectedProduct?.variants?.length + 1.5}
              customCSS="pr-3"
              shouldRenderDots={false}>
              {selectedProduct?.variants?.map(
                (productVariant: DiscountedProductSelectorProductVariantType, vIndex: number) => {
                  return (
                    <DiscountedVariantCard
                      productVariant={productVariant}
                      variantIndex={vIndex}
                      groupIndex={groupIndex}
                      productIndex={productIndex}
                    />
                  );
                },
              )}
            </ListCarousel>
          </div>
        </div>
      )}
    </li>
  );
};

export default DiscountedProductCard;
